import React, {useState, useEffect, useRef, useCallback} from 'react'
import io from 'socket.io-client'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {toast} from 'react-toastify'
import GenericTable from '../components/shared/generic-table'
import {useAuthClient} from '../utils/use-auth-client'
import '../styles/control-center-styles.css'
import {ReactComponent as RefreshIcon} from '../assets/refresh-icon.svg'
import {OOE_SERVICE_URL as REACT_APP_OOE_SERVICE_URL} from '../utils/api-base'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const DEFAULT_TIMEZONE = 'America/Chicago'

const formatDate = dateStr =>
  dayjs(dateStr).tz(DEFAULT_TIMEZONE).format('MM/DD/YYYY hh:mmA z')

const CountdownTimer = ({expiryDate}) => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!expiryDate) return

    const calculate = () => {
      const diff = dayjs(expiryDate).diff(dayjs())
      if (diff <= 0) {
        setTimeLeft('Expired')
        return
      }
      const d = Math.floor(diff / 86400000)
      const h = Math.floor((diff % 86400000) / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setTimeLeft(
        `${String(d).padStart(2, '0')}d : ${String(h).padStart(
          2,
          '0',
        )}h : ${String(m).padStart(2, '0')}m : ${String(s).padStart(2, '0')}s`,
      )
    }

    calculate()
    const timer = setInterval(calculate, 1000)
    return () => clearInterval(timer)
  }, [expiryDate])

  if (!expiryDate) return 'N/A'

  const isExpired = dayjs(expiryDate).isBefore(dayjs())

  return (
    <p
      style={{
        fontFamily: 'monospace',
        fontWeight: 'bold',
        margin: 0,
        whiteSpace: 'nowrap',
        color: isExpired ? '#cc0000' : 'inherit',
      }}
    >
      {timeLeft}
    </p>
  )
}

const useDebouncedValue = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const inputStyle = {
  padding: '10px 12px',
  borderRadius: '4px',
  border: '1px solid #d4d4d4',
  fontSize: '14px',
  fontFamily: 'Open Sans, sans-serif',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#ffffff',
  color: '#231f20',
  transition: 'border-color 0.2s',
}

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  marginBottom: '6px',
  fontWeight: '600',
  textTransform: 'uppercase',
  color: '#666',
  letterSpacing: '0.5px',
}

// ─── View Status Modal (Timeline) ─────────────────────────────────────────────

const INITIAL_STEPS = [
  {label: 'Payment Link Sent'},
  {label: 'Client Clicked Link'},
  {label: 'Payment Method Added'},
  {label: 'Proceed with Order'},
]

const TimelineSkeleton = () => (
  <>
    <style>{`
      @keyframes shimmer {
        0% { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }
      .tl-skeleton {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 800px 100%;
        animation: shimmer 1.4s infinite linear;
        border-radius: 4px;
      }
    `}</style>
    <div style={{position: 'relative', paddingLeft: '32px'}}>
      <div
        style={{
          position: 'absolute',
          left: '11px',
          top: '8px',
          bottom: '8px',
          width: '2px',
          backgroundColor: '#e3e6e8',
        }}
      />
      {INITIAL_STEPS.map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: i < INITIAL_STEPS.length - 1 ? '28px' : 0,
            position: 'relative',
          }}
        >
          <div
            className="tl-skeleton"
            style={{
              position: 'absolute',
              left: '-26px',
              top: '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
            }}
          />
          <div
            className="tl-skeleton"
            style={{width: '120px', height: '14px'}}
          />
          <div
            className="tl-skeleton"
            style={{width: '80px', height: '11px', marginTop: '6px'}}
          />
        </div>
      ))}
    </div>
  </>
)

const ViewStatusModal = ({link, steps, loading, onClose}) => {
  if (!link) return null

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '32px',
          zIndex: 1001,
          width: '480px',
          maxWidth: '95vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <div>
            <h2 style={{margin: 0, fontSize: '18px', fontWeight: '700'}}>
              Payment Link Timeline
            </h2>
            <p style={{margin: '4px 0 0', fontSize: '13px', color: '#666'}}>
              {link.company_name} &mdash; Order #{link.order_id}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: '#666',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        {loading ? (
          <TimelineSkeleton />
        ) : (
          /* Timeline */
          <div style={{position: 'relative', paddingLeft: '32px'}}>
            {/* vertical line */}
            <div
              style={{
                position: 'absolute',
                left: '11px',
                top: '8px',
                bottom: '8px',
                width: '2px',
                backgroundColor: '#e3e6e8',
              }}
            />

            {steps.map((step, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: i < steps.length - 1 ? '28px' : 0,
                  position: 'relative',
                }}
              >
                {/* dot */}
                <div
                  style={{
                    position: 'absolute',
                    left: '-26px',
                    top: '2px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: step.completed ? '#fa6000' : '#e3e6e8',
                    border: `2px solid ${
                      step.completed ? '#fa6000' : '#c0c0c0'
                    }`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {step.completed && (
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path
                        d="M1 4L3 6L7 2"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>

                <span
                  style={{
                    fontWeight: '600',
                    fontSize: '14px',
                    color: step.completed ? '#231f20' : '#aaa',
                  }}
                >
                  {step.label}
                </span>

                {step.timestamp ? (
                  <span
                    style={{fontSize: '12px', color: '#888', marginTop: '2px'}}
                  >
                    {formatDate(step.timestamp)}
                  </span>
                ) : (
                  <span
                    style={{fontSize: '12px', color: '#ccc', marginTop: '2px'}}
                  >
                    Pending
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

// ─── Resend Link Modal ────────────────────────────────────────────────────────

const ResendLinkModal = ({link, onClose, onResend, client}) => {
  const [method, setMethod] = useState(null)
  const [sending, setSending] = useState(false)

  if (!link) return null

  const handleSend = async () => {
    if (!method || sending) return
    setSending(true)
    try {
      const is4dot0 = link.flow_version === '4.0'
      const endpoint = is4dot0
        ? 'orders/process/resend-link'
        : 'cc/order/resend-link'
      const response = await client(endpoint, {
        method: 'PATCH',
        data: {
          orderId: String(link.order_id),
          paymentUuid: link.payment_uuid,
          method: method.toLowerCase(),
          ...(method === 'Email'
            ? {email: link.client_email}
            : {contactPhone: link.contact_phone, mobile: link.contact_phone}),
          country: link.business_country,
          appUrl: window.location.origin,
          ...(link.order_header_id
            ? {orderHeaderId: link.order_header_id}
            : {}),
        },
      })
      toast.success('Payment link resent successfully')
      onResend(response?.newUuid || response?.randomId)
      onClose()
    } catch (err) {
      console.error('resend-link error:', err)
      toast.error('Failed to resend link. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '32px',
          zIndex: 1001,
          width: '400px',
          maxWidth: '95vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h2 style={{margin: 0, fontSize: '18px', fontWeight: '700'}}>
            Resend Payment Link
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: '#666',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        <p style={{fontSize: '13px', color: '#666', marginBottom: '20px'}}>
          Choose how to resend the link to{' '}
          <strong>
            {method === 'SMS' ? link.contact_phone : link.client_email}
          </strong>
        </p>

        {/* Method selection */}
        <div style={{display: 'flex', gap: '12px', marginBottom: '24px'}}>
          {['Email', 'SMS'].map(opt => (
            <button
              key={opt}
              onClick={() => setMethod(opt)}
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: '6px',
                border: `2px solid ${method === opt ? '#fa6000' : '#d4d4d4'}`,
                backgroundColor: method === opt ? '#fff5f0' : '#fff',
                color: method === opt ? '#fa6000' : '#666',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {opt}
            </button>
          ))}
        </div>

        {/* Send button */}
        <button
          disabled={!method || sending}
          onClick={handleSend}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: !method || sending ? '#ccc' : '#fa6000',
            color: '#fff',
            fontWeight: '700',
            fontSize: '14px',
            cursor: !method || sending ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
          }}
        >
          {sending ? 'Sending...' : 'Send Link'}
        </button>
      </div>
    </>
  )
}

// ─── Order Details Modal ──────────────────────────────────────────────────────

const InfoRow = ({label, value}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '14px',
    }}
  >
    <span style={{fontWeight: '600', color: '#444'}}>{label}</span>
    <span
      style={{
        color: '#231f20',
        textAlign: 'right',
        maxWidth: '60%',
        wordBreak: 'break-all',
      }}
    >
      {value}
    </span>
  </div>
)

const Section = ({label, children}) => (
  <div style={{marginTop: '20px'}}>
    <h4
      style={{
        fontSize: '12px',
        fontWeight: '700',
        textTransform: 'uppercase',
        color: '#888',
        letterSpacing: '0.5px',
        margin: '0 0 8px',
      }}
    >
      {label}
    </h4>
    {children}
  </div>
)

const DetailsModal = ({order, onClose}) => {
  if (!order) return null

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '32px',
          zIndex: 1001,
          width: '560px',
          maxWidth: '95vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <div>
            <h2 style={{margin: 0, fontSize: '18px', fontWeight: '700'}}>
              Order Details
            </h2>
            <p style={{margin: '4px 0 0', fontSize: '13px', color: '#666'}}>
              Order #{order.order_id}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: '#666',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        {/* Client Info */}
        <Section label="Client Info">
          {[
            {
              label: 'Name',
              value: [order.first_name, order.last_name]
                .filter(Boolean)
                .join(' '),
            },
            {label: 'Email', value: order.client_email},
            {label: 'Phone', value: order.contact_phone},
            {label: 'Company', value: order.company_name},
            {label: 'Country', value: order.business_country},
            {label: 'Thryv ID', value: order.thryv_id},
          ].map(({label, value}) =>
            value ? <InfoRow key={label} label={label} value={value} /> : null,
          )}
        </Section>

        {/* Link Info */}
        <Section label="Link Info">
          {[
            {
              label: 'Created',
              value: order.created ? formatDate(order.created) : null,
            },
            {
              label: 'Updated',
              value: order.updated_date ? formatDate(order.updated_date) : null,
            },
            {label: 'Flow', value: order.flow},
            {label: 'Version', value: order.flow_version},
          ].map(({label, value}) =>
            value ? <InfoRow key={label} label={label} value={value} /> : null,
          )}
        </Section>

        {/* Rep / Creator Info */}
        <Section label="Created By">
          {[{label: 'User ID', value: order.rep_id}].map(({label, value}) =>
            value ? <InfoRow key={label} label={label} value={value} /> : null,
          )}
        </Section>

        {/* Notes */}
        {order.order_notes && (
          <Section label="Order Notes">
            <p
              style={{
                margin: 0,
                fontSize: '13px',
                color: '#444',
                lineHeight: '1.5',
              }}
            >
              {order.order_notes}
            </p>
          </Section>
        )}
      </div>
    </>
  )
}

// ─── Action Cell (reactive expiry) ───────────────────────────────────────────

const ActionCell = ({
  row,
  onViewDetails,
  onViewStatus,
  onResend,
  isOrderComplete,
}) => {
  const [isExpired, setIsExpired] = useState(
    row.expires ? dayjs(row.expires).isBefore(dayjs()) : false,
  )

  useEffect(() => {
    if (!row.expires || isExpired) return
    const timer = setInterval(() => {
      if (dayjs(row.expires).isBefore(dayjs())) {
        setIsExpired(true)
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [row.expires, isExpired])

  // Mirrors PaymentDetailsStep's isOrderComplete check — once the order is
  // confirmed there's nothing left to send/resend a payment link for.
  const isResendDisabled = isExpired || isOrderComplete

  return (
    <div style={{display: 'flex', gap: '8px', whiteSpace: 'nowrap'}}>
      <button
        className="table-button"
        onClick={e => {
          e.stopPropagation()
          onViewDetails(row)
        }}
      >
        Details
      </button>
      <button
        className="table-button"
        onClick={e => {
          e.stopPropagation()
          onViewStatus(row)
        }}
      >
        View Status
      </button>
      {row.expires && (
        <button
          className="table-button copy-link"
          disabled={isResendDisabled}
          onClick={e => {
            e.stopPropagation()
            if (!isResendDisabled) onResend(row)
          }}
          title={isOrderComplete ? 'Order is already complete' : undefined}
          style={
            isResendDisabled
              ? {opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none'}
              : {}
          }
        >
          Resend Link
        </button>
      )}
    </div>
  )
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

const COUNT_PER_PAGE = 15

function PendingLinks() {
  const client = useAuthClient()

  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [viewStatusLink, setViewStatusLink] = useState(null)
  const [viewStatusSteps, setViewStatusSteps] = useState([])
  const [isTimelineLoading, setIsTimelineLoading] = useState(false)
  // payment_uuids learned (via the timeline fetch or a live socket update) to
  // have reached the final "Proceed with Order" step — used to disable that
  // row's Resend Link button, same as PaymentDetailsStep's isOrderComplete.
  const [completedUuids, setCompletedUuids] = useState(() => new Set())
  const [resendLink, setResendLink] = useState(null)
  const [detailsOrder, setDetailsOrder] = useState(null)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const dateRangeRef = useRef(null)
  const socketRef = useRef(null)
  const connectedUuidRef = useRef(null)

  useEffect(() => {
    if (!viewStatusLink) return
    const uuid = viewStatusLink.payment_uuid

    const baseSteps = INITIAL_STEPS.map((s, i) => ({
      ...s,
      completed: i === 0,
      timestamp: i === 0 ? viewStatusLink.created : null,
    }))

    const applyPersistedSteps = (steps, persisted) => {
      const updated = steps.map(s => ({...s}))
      for (const {step_number, completed_at} of persisted) {
        // step_number from the server is 1-indexed (1..4); INITIAL_STEPS is
        // 0-indexed (0..3), so mark up to step_number - 1, not step_number.
        for (let i = 0; i < step_number; i++) {
          if (!updated[i]?.completed) {
            updated[i] = {
              ...updated[i],
              completed: true,
              timestamp: completed_at,
            }
          }
        }
      }
      return updated
    }

    // Load persisted steps from the server first
    setIsTimelineLoading(true)
    client(`cc/order/timeline/${uuid}`)
      .then(data => {
        const steps =
          data.success && data.steps?.length
            ? applyPersistedSteps(baseSteps, data.steps)
            : baseSteps
        setViewStatusSteps(steps)
        if (steps[steps.length - 1]?.completed) {
          setCompletedUuids(prev => new Set(prev).add(uuid))
        }
      })
      .catch(() => {
        setViewStatusSteps(baseSteps)
      })
      .finally(() => {
        setIsTimelineLoading(false)
      })

    if (uuid === connectedUuidRef.current) return

    if (socketRef.current) socketRef.current.close()
    connectedUuidRef.current = uuid

    if (!uuid) return

    const socket = io(REACT_APP_OOE_SERVICE_URL, {transports: ['websocket']})
    socketRef.current = socket
    socket.emit('join', {uuid}, () => {})
    socket.on('stepTaken', ({stepNumber}) => {
      setViewStatusSteps(prev => {
        const updated = prev.map(s => ({...s}))
        // stepNumber is 1-indexed (1..4); see applyPersistedSteps above.
        for (let i = 0; i < stepNumber; i++) {
          if (!updated[i]?.completed) {
            updated[i] = {
              ...updated[i],
              completed: true,
              timestamp: new Date().toISOString(),
            }
          }
        }
        if (updated[updated.length - 1]?.completed) {
          setCompletedUuids(prevUuids => new Set(prevUuids).add(uuid))
        }
        return updated
      })
    })
    socket.on('error-message', ({message}) => toast.error(message))
  }, [viewStatusLink, client])

  useEffect(() => {
    return () => {
      if (socketRef.current) socketRef.current.close()
    }
  }, [])

  const [filters, setFilters] = useState({
    companyName: '',
    clientEmail: '',
    thryvId: '',
    startDate: '',
    endDate: '',
  })

  const debouncedCompanyName = useDebouncedValue(filters.companyName)
  const debouncedClientEmail = useDebouncedValue(filters.clientEmail)
  const debouncedThryvId = useDebouncedValue(filters.thryvId)
  const debouncedStartDate = useDebouncedValue(filters.startDate)
  const debouncedEndDate = useDebouncedValue(filters.endDate)

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value}
      if (key === 'startDate' && prev.endDate && value > prev.endDate) {
        updated.endDate = ''
      }
      return updated
    })
  }

  const clearFilters = () => {
    setFilters({
      companyName: '',
      clientEmail: '',
      thryvId: '',
      startDate: '',
      endDate: '',
    })
    setIsDateRangeOpen(false)
    setPage(1)
  }

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        isDateRangeOpen &&
        dateRangeRef.current &&
        !dateRangeRef.current.contains(e.target)
      ) {
        setIsDateRangeOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDateRangeOpen])

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setPage(1)
  }, [
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  const fetchPendingLinks = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: COUNT_PER_PAGE.toString(),
      })
      if (debouncedCompanyName)
        params.append('companyName', debouncedCompanyName)
      if (debouncedClientEmail)
        params.append('clientEmail', debouncedClientEmail)
      if (debouncedThryvId) params.append('thryvId', debouncedThryvId)
      if (debouncedStartDate) params.append('startDate', debouncedStartDate)
      if (debouncedEndDate) params.append('endDate', debouncedEndDate)

      const data = await client(`cc/order/pending-links?${params}`)

      if (data.success && data.response) {
        setRows(data.response.data || [])
        if (data.response.pagination) {
          setPagination({
            page: data.response.pagination.page,
            totalPages: data.response.pagination.totalPages,
            total: data.response.pagination.total,
            hasNext: data.response.pagination.hasNext,
            hasPrev: data.response.pagination.hasPrev,
          })
        }
      }
    } catch (err) {
      console.error('Error fetching pending links:', err)
      toast.error('Error fetching pending links')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [
    client,
    page,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  useEffect(() => {
    fetchPendingLinks()
  }, [fetchPendingLinks])

  const handleViewDetails = row => {
    setDetailsOrder(row)
  }

  const handleDateRangeChange = value => {
    if (Array.isArray(value)) {
      const [start, end] = value
      updateFilter('startDate', start ? dayjs(start).format('YYYY-MM-DD') : '')
      updateFilter('endDate', end ? dayjs(end).format('YYYY-MM-DD') : '')
      if (start && end) setIsDateRangeOpen(false)
    }
  }

  const getCalendarValue = () => {
    if (!filters.startDate && !filters.endDate) return null
    const toLocal = str => {
      if (!str) return null
      const [y, m, d] = str.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    return [toLocal(filters.startDate), toLocal(filters.endDate)]
  }

  const dateRangeLabel = filters.startDate
    ? filters.endDate
      ? `${dayjs(filters.startDate).format('MM/DD/YYYY')} - ${dayjs(
          filters.endDate,
        ).format('MM/DD/YYYY')}`
      : `${dayjs(filters.startDate).format('MM/DD/YYYY')} - Select end date`
    : ''

  const columns = [
    {key: 'order_id', label: 'Order ID'},
    {key: 'company_name', label: 'Company Name'},
    {
      key: 'created',
      label: 'Created',
      render: date => (
        <p style={{margin: 0, fontSize: '12px', whiteSpace: 'nowrap'}}>
          {formatDate(date)}
        </p>
      ),
    },
    {
      key: 'client_email',
      label: 'Client Email',
      render: email => email || 'N/A',
    },
    {
      key: 'expires',
      label: 'Expires',
      render: date => (
        <p style={{margin: 0, whiteSpace: 'nowrap', fontSize: '12px'}}>
          {date ? formatDate(date) : 'N/A'}
        </p>
      ),
    },
    {
      key: 'expires',
      label: 'Time Left',
      render: date => <CountdownTimer expiryDate={date} />,
    },
    {
      key: 'actions',
      label: 'Action',
      render: (_, row) => (
        <ActionCell
          row={row}
          onViewDetails={handleViewDetails}
          onViewStatus={setViewStatusLink}
          onResend={setResendLink}
          isOrderComplete={completedUuids.has(row.payment_uuid)}
        />
      ),
    },
  ]

  const displayStart = pagination.total
    ? (pagination.page - 1) * COUNT_PER_PAGE + 1
    : 0
  const displayEnd = Math.min(
    pagination.page * COUNT_PER_PAGE,
    pagination.total,
  )

  return (
    <div style={{padding: '24px'}}>
      {/* Page header */}
      <h3 className="control-center-item-header" style={{marginBottom: '8px'}}>
        Pending Purchase Links
      </h3>

      {pagination.total > 0 && (
        <h4
          className="control-center-display-text"
          style={{marginBottom: '16px'}}
        >
          Displaying {displayStart}-{displayEnd} out of {pagination.total}
        </h4>
      )}

      {/* Filters */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          border: '1px solid #e9e9e9',
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <div style={{flex: '1 1 180px', minWidth: '160px'}}>
          <label style={labelStyle}>Company Name</label>
          <input
            type="text"
            placeholder="Filter by company"
            value={filters.companyName}
            onChange={e => updateFilter('companyName', e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#fa6000')}
            onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
          />
        </div>

        <div style={{flex: '1 1 200px', minWidth: '180px'}}>
          <label style={labelStyle}>Client Email</label>
          <input
            type="text"
            placeholder="Filter by email"
            value={filters.clientEmail}
            onChange={e => updateFilter('clientEmail', e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#fa6000')}
            onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
          />
        </div>

        <div style={{flex: '1 1 160px', minWidth: '140px'}}>
          <label style={labelStyle}>Thryv ID</label>
          <input
            type="text"
            placeholder="Filter by Thryv ID"
            value={filters.thryvId}
            onChange={e => updateFilter('thryvId', e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#fa6000')}
            onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
          />
        </div>

        <div style={{flex: '1 1 260px', minWidth: '220px'}} ref={dateRangeRef}>
          <label style={labelStyle}>Date Range</label>
          <div style={{position: 'relative'}}>
            <input
              type="text"
              readOnly
              value={dateRangeLabel}
              placeholder="Select date range"
              onClick={() => setIsDateRangeOpen(prev => !prev)}
              style={{...inputStyle, cursor: 'pointer'}}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
            {isDateRangeOpen && (
              <div
                style={{
                  position: 'absolute',
                  zIndex: 20,
                  marginTop: '8px',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <Calendar
                  selectRange
                  onChange={handleDateRangeChange}
                  value={getCalendarValue()}
                />
              </div>
            )}
          </div>
        </div>

        <div style={{flex: '0 0 auto', display: 'flex', gap: '8px'}}>
          <label style={{...labelStyle, color: 'transparent'}}>&nbsp;</label>
          <button
            onClick={clearFilters}
            style={{
              padding: '10px 16px',
              borderRadius: '4px',
              border: '1px solid #d4d4d4',
              fontSize: '14px',
              fontFamily: 'Open Sans, sans-serif',
              backgroundColor: '#ffffff',
              color: '#fa6000',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#fa6000'
              e.currentTarget.style.color = '#ffffff'
              e.currentTarget.style.borderColor = '#fa6000'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff'
              e.currentTarget.style.color = '#fa6000'
              e.currentTarget.style.borderColor = '#d4d4d4'
            }}
          >
            Clear Filters
          </button>
          <button
            onClick={fetchPendingLinks}
            disabled={isLoading}
            className="refresh-button"
            style={{
              padding: '10px 16px',
              borderRadius: '4px',
              border: '1px solid #d4d4d4',
              fontSize: '14px',
              fontFamily: 'Open Sans, sans-serif',
              backgroundColor: '#ffffff',
              color: isLoading ? '#aaa' : '#fa6000',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshIcon
              width="14"
              height="14"
              style={{
                animation: isLoading ? 'spin 0.8s linear infinite' : 'none',
              }}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div
          style={{
            textAlign: 'center',
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        >
          <p style={{color: '#808080', fontSize: '14px'}}>Loading...</p>
        </div>
      ) : rows.length > 0 ? (
        <div
          className="control-center-table-wrapper"
          style={{overflowX: 'auto'}}
        >
          <style>{`.pending-links-table td:last-child, .pending-links-table th:last-child { max-width: none !important; white-space: nowrap; }`}</style>
          <div className="pending-links-table">
            <GenericTable
              columns={columns}
              rows={rows}
              emptyMessage="No pending links found."
              pagination={{
                page: pagination.page,
                totalPages: pagination.totalPages,
                hasPrev: pagination.hasPrev,
                hasNext: pagination.hasNext,
                onPageChange: setPage,
              }}
            />
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        >
          <h4 style={{color: '#808080', fontSize: '16px', fontWeight: '600'}}>
            No Pending Purchase Links found
          </h4>
          <p style={{color: '#999', fontSize: '14px', marginTop: '8px'}}>
            Try adjusting your filters to see more results
          </p>
        </div>
      )}

      {/* Modals */}
      <DetailsModal
        order={detailsOrder}
        onClose={() => setDetailsOrder(null)}
      />
      <ViewStatusModal
        link={viewStatusLink}
        steps={viewStatusSteps}
        loading={isTimelineLoading}
        onClose={() => setViewStatusLink(null)}
      />
      <ResendLinkModal
        link={resendLink}
        onClose={() => setResendLink(null)}
        onResend={newUuid => {
          if (socketRef.current) socketRef.current.close()
          socketRef.current = null
          connectedUuidRef.current = null
          if (newUuid) {
            setRows(prev =>
              prev.map(r =>
                r.payment_uuid === resendLink?.payment_uuid
                  ? {...r, payment_uuid: newUuid}
                  : r,
              ),
            )
          }
          setViewStatusSteps(
            INITIAL_STEPS.map((s, i) => ({
              ...s,
              completed: i === 0,
              timestamp: i === 0 ? resendLink?.created : null,
            })),
          )
        }}
        client={client}
      />
    </div>
  )
}

export {PendingLinks}
